/*
 * AboutPage.tsx
 * Public product/marketing page served at /about (no auth required).
 * Covers the Trial, Solo, Team, and Enterprise subscription tiers.
 * Uses standalone styles (AboutPage.css) with ab- prefix to avoid Bootstrap conflicts.
 */

import { Link } from 'react-router-dom';
import './AboutPage.css';
import {
  InboxMockup,
  RulesMockup,
  SLAMockup,
  SLAConfigMockup,
  DigestMockup,
  SuggestionChips,
} from './AboutPage.Mockups';
import { PublicNav } from '../components/public/PublicNav';
import { PublicFooter } from '../components/public/PublicFooter';

export default function AboutPage() {
  return (
    <div className="ab-page">

      {/* ── Sticky nav ─────────────────────────────────────────────────── */}
      <PublicNav />

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className="ab-hero">
        <div className="ab-container">
          <img
            src="/logo_hero.png"
            alt="Catty — your email sorting assistant"
            style={{ width: 220, height: 'auto', borderRadius: 16, marginBottom: 28, boxShadow: '0 12px 48px rgba(0,0,0,0.4)' }}
          /> <br/>
          <span className="ab-eyebrow">Microsoft 365 inbox intelligence</span>
          <h1>Your inbox, <span>automatically</span> organised.</h1>
          <p className="ab-subtitle">
            Catty connects to Microsoft Outlook via the Microsoft Graph API, analyses
            your email threads in real time, and applies intelligent categories and
            mailbox rules — so you always know what needs attention and what can wait.
          </p>
          <div className="ab-cta-group">
            <a className="ab-btn ab-btn-primary" href="#ab-pricing">See pricing</a>
            <a className="ab-btn ab-btn-outline" href="#ab-how-it-works">How it works</a>
          </div>
          <div className="ab-badge-strip">
            <span className="ab-badge">Sign in with Microsoft</span>
            <span className="ab-badge">Works in Outlook — no plugin needed</span>
            <span className="ab-badge">14-day free trial — no card required</span>
            <span className="ab-badge">Individual and team plans</span>
          </div>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────────────── */}
      <section id="ab-features" className="ab-section">
        <div className="ab-container">
          <span className="ab-section-label">What Catty does</span>
          <h2>Get clarity across every inbox</h2>
          <p className="ab-section-intro">
            Catty works silently in the background. The moment a new email arrives,
            it reads the thread, decides what it is, and keeps your Outlook categories
            up to date — no manual tagging, no new apps to open.
          </p>

          <div className="ab-feature-grid">
            <div className="ab-feature-card">
              <div className="ab-icon ab-icon-green">🧠</div>
              <h3>AI categorisation</h3>
              <p>
                Every conversation is classified automatically using a protected, state-of-the-art language model. Categories reflect your
                organisation's actual language and workflows, not a generic template.
              </p>
              <InboxMockup rows={[
                { from: 'Alice Chen', subject: 'Q2 budget sign-off', tags: ['To respond'], time: '09:14', read: false },
                { from: 'Stripe', subject: 'Invoice #1042 — payment confirmed', tags: ['Notification'], time: 'Mon', read: true },
                { from: 'Bob — PartnerCo', subject: 'Partnership proposal', tags: ['Awaiting reply'], time: 'Fri', read: true, replied: true },
              ]} />
            </div>

            <div className="ab-feature-card">
              <div className="ab-icon ab-icon-blue">🏷️</div>
              <h3>Multi-dimensional tagging</h3>
              <p>
                Assign separate, mutually-exclusive labels across orthogonal
                dimensions — action status, topic, client group, or any custom
                dimension you define. An email can be <em>Awaiting reply</em>{' '}
                <strong>and</strong> <em>Finance</em> simultaneously.
              </p>
              <InboxMockup rows={[
                { from: 'Alice Chen', subject: 'Q2 budget sign-off', tags: ['To respond', 'Finance'], time: '09:14', read: false },
                { from: 'Stripe', subject: 'Invoice #1042 — payment confirmed', tags: ['Notification', 'Accounts'], time: 'Mon', read: true },
                { from: 'Bob — PartnerCo', subject: 'Partnership proposal', tags: ['Actioned'], time: 'Fri', read: true, replied: true },
              ]} highlight="Actioned" />
            </div>

            <div className="ab-feature-card">
              <div className="ab-icon ab-icon-gold">✅</div>
              <h3>Mailbox rules engine</h3>
              <p>
                Rules fire automatically when a conversation is categorised: move it
                to a folder, flag it, mark it read, or run a custom action. Rules
                combine AI categories with sender patterns for surgical precision.
              </p>
              <RulesMockup rules={[
                {
                  conditions: [
                    { field: 'Category', op: '=', value: 'Notification' },
                    { field: 'received', op: '>', value: '3 days ago' },
                  ],
                  action: 'Move to Archive',
                },
                {
                  conditions: [
                    { field: 'Category', op: '=', value: 'Actioned' },
                    { field: 'Sender domain', op: '=', value: '*@acme.com' },
                  ],
                  action: 'Move to folder `Archive – Acme`',
                },
              ]} />
            </div>

            <div className="ab-feature-card">
              <div className="ab-icon ab-icon-blue">📊</div>
              <h3>SLA &amp; response-time tracking</h3>
              <p>
                Define per-category response windows. Catty flags threads that are
                drifting towards a breach and surfaces overdue items in your daily
                summary, so nothing slips through the net.
              </p>
              <SLAConfigMockup
                client="Acme Corp"
                windows={[
                  { condition: 'Received before 12:00', deadline: 'Reply same day', colour: 'green' },
                  { condition: 'Received from 12:00', deadline: 'Reply next working day', colour: 'amber' },
                ]}
              />
              <SLAMockup />
            </div>

            <div className="ab-feature-card">
              <div className="ab-icon ab-icon-orange">📋</div>
              <h3>Daily digest &amp; summary</h3>
              <p>
                Start each morning with a concise, structured digest of your inbox.
                Outstanding actions, awaited replies, and newly-arrived threads —
                all grouped, all scannable in under two minutes.
              </p>
              <DigestMockup />
            </div>

            <div className="ab-feature-card">
              <div className="ab-icon ab-icon-gold">💡</div>
              <h3>Smart setup suggestions</h3>
              <p>
                Catty analyses your inbox history and proposes a ready-made category
                structure and rule set tailored to your actual email patterns. Review,
                tweak, and apply with one click — no configuration from scratch.
              </p>
              <SuggestionChips />
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ───────────────────────────────────────────────── */}
      <section id="ab-how-it-works" className="ab-section ab-section-alt">
        <div className="ab-container">
          <span className="ab-section-label">How it works</span>
          <h2>Up and running in minutes</h2>
          <p className="ab-section-intro">
            Catty is entirely cloud-native. There is nothing to install.
            It connects to your Microsoft 365 account via official Graph API
            delegated permissions only.
          </p>

          <div className="ab-steps">
            <div className="ab-step">
              <div className="ab-step-num">1</div>
              <div className="ab-step-connector" />
              <div className="ab-step-content">
                <h3>Sign in with Microsoft</h3>
                <p>
                  Use your existing Microsoft 365 account — no Catty username or
                  password is ever created. You can grant the required Microsoft
                  Graph permissions during sign-in and continue immediately.
                </p>
              </div>
            </div>

            <div className="ab-step">
              <div className="ab-step-num">2</div>
              <div className="ab-step-connector" />
              <div className="ab-step-content">
                <h3>Catty analyses your inbox</h3>
                <p>
                  Within minutes, Catty reads your recent email history and produces
                  a free inbox analysis: traffic patterns, dominant sender groups,
                  common thread types, and a suggested category structure tailored to
                  your actual workload.
                </p>
              </div>
            </div>

            <div className="ab-step">
              <div className="ab-step-num">3</div>
              <div className="ab-step-connector" />
              <div className="ab-step-content">
                <h3>Review and configure your categories</h3>
                <p>
                  Accept Catty's suggestions or customise them fully. Define as many
                  category dimensions as you need. Organisation admins can publish
                  shared category sets across the team, with per-user overrides where
                  individuals need flexibility.
                </p>
              </div>
            </div>

            <div className="ab-step">
              <div className="ab-step-num">4</div>
              <div className="ab-step-connector" />
              <div className="ab-step-content">
                <h3>Automation runs continuously</h3>
                <p>
                  From the moment you enable automation, every incoming email is
                  classified within seconds. Your Outlook categories stay current
                  without any manual effort. Mailbox rules fire immediately after
                  categorisation, keeping your folders tidy automatically.
                </p>
              </div>
            </div>

            <div className="ab-step">
              <div className="ab-step-num">5</div>
              <div className="ab-step-content">
                <h3>Track and refine over time</h3>
                <p>
                  Review categorisation accuracy, SLA performance, and inbox metrics
                  via the Catty dashboard. Adjust your category hints and rules at any
                  time — changes take effect on the very next email in.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Organisations ──────────────────────────────────────────────── */}
      <section id="ab-organisations" className="ab-section">
        <div className="ab-container">
          <span className="ab-section-label">For teams &amp; organisations</span>
          <h2>Shared standards, individual flexibility</h2>
          <p className="ab-section-intro">
            Catty supports a hybrid model: organisation admins define the categories
            and rules that matter company-wide, while individual users can layer their
            own personal overrides on top.
          </p>

          <table className="ab-org-table">
            <thead>
              <tr>
                <th>Capability</th>
                <th>Org admin</th>
                <th>Individual user</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Define shared category sets &amp; dimensions</td>
                <td>Yes — published to all members</td>
                <td>Can add personal overrides</td>
              </tr>
              <tr>
                <td>Set mailbox rules</td>
                <td>Yes — org-wide rules apply first</td>
                <td>Yes — personal rules apply on top</td>
              </tr>
              <tr>
                <td>Configure SLA windows per category</td>
                <td>Yes — sets baseline for the org</td>
                <td>Can adjust for their own mailbox</td>
              </tr>
              <tr>
                <td>View suggested rules from inbox analysis</td>
                <td>Org-level suggestions</td>
                <td>Individual inbox suggestions</td>
              </tr>
              <tr>
                <td>Manage billing &amp; seats</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>Add or remove members</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────────────── */}
      <section id="ab-pricing" className="ab-section ab-section-alt">
        <div className="ab-container">
          <span className="ab-section-label">Pricing</span>
          <h2>Try free, then pay only for what you use</h2>
          <p className="ab-section-intro">
            Every account starts with a 14-day free trial — no credit card required.
            After the trial, choose a plan that fits your team. All prices include UK VAT.
          </p>

          <div className="ab-pricing-grid">
            {/* Trial */}
            <div className="ab-plan">
              <p className="ab-plan-label">Trial</p>
              <h3>Free Trial</h3>
              <div className="ab-price"><sup>£</sup>0</div>
              <p className="ab-price-note">14 days or 1,000 emails — no card required</p>
              <ul>
                <li className="ab-check">1 mailbox</li>
                <li className="ab-check">Real-time AI categorisation</li>
                <li className="ab-check">Automated mailbox rules engine</li>
                <li className="ab-check">Daily digest &amp; inbox summary</li>
                <li className="ab-check">Sign in with Microsoft — no password</li>
              </ul>
              <Link to="/login?plan=trial" className="ab-plan-cta">Start free trial</Link>
            </div>

            {/* Solo */}
            <div className="ab-plan ab-highlight">
              <p className="ab-plan-label ab-plan-label-featured">Solo</p>
              <h3>Individual</h3>
              <div className="ab-price"><sup>£</sup>10</div>
              <p className="ab-price-note">per mailbox / month · additional mailboxes £6/month</p>
              <ul>
                <li className="ab-check">Everything in Trial</li>
                <li className="ab-check">Multiple monitored mailboxes</li>
                <li className="ab-check">Up to 3 category sets</li>
                <li className="ab-check">SLA &amp; response-time tracking</li>
                <li className="ab-check">AI-suggested rule improvements</li>
              </ul>
              <Link to="/login?plan=solo" className="ab-plan-cta">Get started</Link>
            </div>

            {/* Team */}
            <div className="ab-plan ab-plan-coming-soon">
              <div className="ab-ribbon">Coming soon</div>
              <p className="ab-plan-label">Team</p>
              <h3>Organisation</h3>
              <div className="ab-price"><sup>£</sup>12</div>
              <p className="ab-price-note">per mailbox / month · minimum 5 mailboxes</p>
              <ul>
                <li className="ab-check">Everything in Solo</li>
                <li className="ab-check">Minimum 5 mailboxes (£60/month min.)</li>
                <li className="ab-check">Up to 10 shared category sets</li>
                <li className="ab-check">Org-wide shared categories &amp; rules</li>
                <li className="ab-check">Search-based mailbox rules</li>
                <li className="ab-check">SLA reports &amp; escalation actions</li>
                <li className="ab-check">Org admin dashboard</li>
              </ul>
            </div>

            {/* Enterprise */}
            <div className="ab-plan ab-plan-coming-soon">
              <div className="ab-ribbon">Coming soon</div>
              <p className="ab-plan-label">Enterprise</p>
              <h3>Bespoke</h3>
              <div className="ab-price" style={{ fontSize: '2rem', paddingTop: 8 }}>Custom</div>
              <p className="ab-price-note">Negotiated pricing · invoiced directly</p>
              <ul>
                <li className="ab-check">Everything in Team</li>
                <li className="ab-check">Unlimited mailboxes</li>
                <li className="ab-check">Unlimited category sets</li>
                <li className="ab-check">Dedicated account manager</li>
                <li className="ab-check">Bespoke contract &amp; SLA</li>
              </ul>
            </div>
          </div>

          <p className="ab-pricing-note">
            Prices include UK VAT. Volume discounts for large enterprise deployments — contact us to discuss.
          </p>
        </div>
      </section>

      {/* ── Trust / Security ───────────────────────────────────────────── */}
      <section className="ab-section">
        <div className="ab-container">
          <span className="ab-section-label">Built for security &amp; compliance</span>
          <h2>No passwords. No data stored unnecessarily.</h2>
          <p className="ab-section-intro">
            Catty is an AWS-native service, hosted entirely in the EU (eu-west-2). It
            accesses your mailbox only via official Microsoft Graph delegated
            permissions — the same mechanism used by Microsoft's own applications.
          </p>

          <div className="ab-feature-grid" style={{ marginTop: 40 }}>
            <div className="ab-feature-card">
              <div className="ab-icon ab-icon-green">🔒</div>
              <h3>Microsoft SSO only</h3>
              <p>
                Catty never sees or stores passwords. Authentication is handled
                entirely by Microsoft Entra ID — the same identity layer your
                organisation already trusts.
              </p>
            </div>

            <div className="ab-feature-card">
              <div className="ab-icon ab-icon-blue">🌍</div>
              <h3>EU-only infrastructure</h3>
              <p>
                All processing and storage runs in AWS eu-west-2 (London). Your
                email metadata never leaves the European Union and is never used
                to train any model. Message content is ephemeral — it's processed live and discarded immediately.
              </p>
            </div>

            <div className="ab-feature-card">
              <div className="ab-icon ab-icon-gold">🧾</div>
              <h3>Minimal permissions</h3>
              <p>
                Catty requests only the Graph scopes it strictly needs — read mail to analyse,
                update categories, and read your folder structure. Nothing more. We don't delete messages.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA footer ─────────────────────────────────────────────────── */}
      <section className="ab-cta-footer">
        <div className="ab-container">
          <h2>Ready to bring order to your inbox?</h2>
          <p>
            The free analysis takes less than five minutes and requires nothing more
            than your Microsoft 365 login. No card, no commitment.
          </p>
          <div className="ab-cta-group">
            <Link to="/login" className="ab-btn ab-btn-primary">Analyse my inbox — it's free</Link>
            <a className="ab-btn ab-btn-outline" href="https://joinedupdata.io/contact/" target="_blank" rel="noopener noreferrer">Talk to us</a>
          </div>
        </div>
      </section>
      {/* ── Site footer ──────────────────────────────────────────────────── */}
      <PublicFooter />
    </div>
  );
}
